import { useEffect, useRef } from 'react';
import { useTrackEvent } from '../context/AnalyticsContext';

export const useViewabilityTracking = (listings, source, additionalData = {}) => {
  const { trackEvent } = useTrackEvent();
  const observerRef = useRef(null);
  const trackedImpressions = useRef(new Set());

  useEffect(() => {
    if (!listings || !listings.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const listingId = entry.target.getAttribute('data-listing-id');
            const position = entry.target.getAttribute('data-position');
            
            // Only track each listing impression once per page load
            const impressionKey = `${listingId}-${position}`;
            if (!trackedImpressions.current.has(impressionKey)) {
              trackedImpressions.current.add(impressionKey);
              
              const listing = listings.find(l => l.id.uuid === listingId);
              if (listing) {
                trackEvent('listing_viewable_impression', {
                  listing_id: listingId,
                  listing_type: listing.attributes.publicData.listingType,
                  position: parseInt(position),
                  artist_id: listing.author?.id?.uuid,
                  price: listing.attributes.price?.amount,
                  currency: listing.attributes.price?.currency,
                  source: source,
                  viewport_time: Date.now(),
                  ...additionalData
                });
              }
            }
          }
        });
      },
      {
        threshold: 0.5, // 50% of the listing must be visible
        rootMargin: '50px 0px', // Start tracking 50px before entering viewport
      }
    );

    observerRef.current = observer;

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      const listingElements = document.querySelectorAll('[data-listing-id]');
      listingElements.forEach(el => observer.observe(el));
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      // Don't clear tracked impressions on cleanup - keep them for the session
    };
  }, [listings, source, trackEvent, JSON.stringify(additionalData)]);

  // Clear tracked impressions when listings change significantly (new search/page)
  useEffect(() => {
    trackedImpressions.current.clear();
  }, [source, additionalData.search_query, additionalData.page_number, additionalData.artist_id]);
}; 